import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/common/components/ui/avatar";
import { Badge } from "@/common/components/ui/badge";
import { Building, GraduationCap, User } from "lucide-react";

export default function StudentHeader({
  universityInfo,
  collegeInfo,
  student,
  data,
}) {
  return (
    <>
      {/* Responsive Header with College and University Logos (interchanged positions) */}
      <div className="flex items-center justify-between pb-4 mb-6 border-b border-gray-200 md:mb-8 md:pb-6">
        {/* College Logo - Left aligned (enhanced for better visibility) */}
        {collegeInfo && (
          <div className="flex items-center">
            <div className="flex items-center justify-center flex-shrink-0 w-56 h-20 p-1 overflow-hidden transition-all bg-white border-2 border-blue-300 rounded-lg shadow-md md:w-64 md:h-24 hover:shadow-lg">
              {collegeInfo.logo ? (
                <img
                  src={collegeInfo.logo}
                  alt={`${collegeInfo.name} Logo`}
                  className="object-contain w-full h-full"
                  title={collegeInfo.name}
                />
              ) : (
                <Building
                  className="w-12 h-12 text-blue-700"
                  title={collegeInfo.name}
                />
              )}
            </div>
          </div>
        )}

        {/* University Logo - Right aligned (now smaller) */}
        <div className="flex items-center">
          <div className="flex items-center justify-center flex-shrink-0 w-24 h-24 p-1 overflow-hidden bg-white border border-gray-200 rounded-full md:w-28 md:h-28">
            {universityInfo.logo ? (
              <img
                src={universityInfo.logo}
                alt={`${universityInfo.name} Logo`}
                className="object-contain w-full h-full"
                title={universityInfo.name}
              />
            ) : (
              <GraduationCap
                className="w-6 h-6 text-blue-700"
                title={universityInfo.name}
              />
            )}
          </div>
        </div>
      </div>

      <div className="mb-6 text-center md:mb-8">
        <h1 className="text-xl font-bold md:text-3xl text-slate-900">
          Student Performance Report
        </h1>
      </div>

      {/* Student Profile Header */}
      <div className="p-4 mb-6 border border-blue-100 rounded-lg md:p-6 md:mb-8 bg-blue-50/30">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <Avatar className="w-16 h-16 border-2 border-blue-100 md:h-20 md:w-20">
              <AvatarImage src={student.avatar} alt={student.name} />
              <AvatarFallback className="text-xl text-blue-700 bg-blue-100">
                {student.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-xl font-bold md:text-2xl text-slate-900">
                {student.name}
              </h2>
              <p className="text-sm md:text-base text-slate-600">
                {student.email}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <div className="flex flex-wrap text-xs gap-x-4 gap-y-1 md:text-sm text-slate-500">
                  {data.gender && (
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span>
                        {data.gender.charAt(0).toUpperCase() +
                          data.gender.slice(1)}
                      </span>
                    </div>
                  )}
                  {data.user_metadata?.map(
                    (meta) =>
                      meta.user_metadata_key?.name === "Department" && (
                        <div key={meta.id} className="flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          <span>{meta.value}</span>
                        </div>
                      )
                  )}
                  {data.user_metadata?.map(
                    (meta) =>
                      meta.user_metadata_key?.name !== "Department" &&
                      meta.user_metadata_key?.name &&
                      meta.value && (
                        <div key={meta.id} className="flex items-center gap-1">
                          <span className="font-medium">
                            {meta.user_metadata_key.name}:
                          </span>
                          <span>{meta.value}</span>
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.college_users?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {data.college_users.map((collegeUser) => (
                  <Badge
                    key={collegeUser.id}
                    variant="outline"
                    className="flex items-center gap-2 px-2 py-1 text-xs text-green-700 bg-green-100 border-green-200 rounded-full md:px-4 md:py-2 md:text-sm"
                  >
                    {collegeUser.college?.logo && (
                      <img
                        src={collegeUser.college.logo}
                        alt={`${collegeUser.college.name} logo`}
                        className="object-contain w-4 h-4 rounded-full md:w-5 md:h-5"
                      />
                    )}
                    {collegeUser.college?.name || "College"}
                  </Badge>
                ))}
              </div>
            )}
            {data.batch_users?.length > 0 && (
              <Badge
                variant="outline"
                className="px-2 py-1 text-xs text-blue-700 bg-blue-100 border-blue-200 rounded-full md:px-4 md:py-2 md:text-sm"
              >
                Batch: {data.batch_users[0].batch?.name || "N/A"}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
